import { useState, useEffect, useRef, useCallback } from "react";
import "./AvatarEditor.css";

const CANVAS_SIZE = 320;
const OUTPUT_SIZE = 400;

function AvatarEditor({ file, onApply, onCancel }) {
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const dragStartRef = useRef(null); // use ref so mousemove closure always has current value
    const [imgLoaded, setImgLoaded] = useState(false);
    const [applying, setApplying] = useState(false);

    // Load image from File
    useEffect(() => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            imgRef.current = img;
            setImgLoaded(true);
        };
        img.src = url;
        return () => URL.revokeObjectURL(url);
    }, [file]);

    // Clamp offset so image always covers the full circle
    const clampOffset = useCallback((ox, oy, currentZoom) => {
        const img = imgRef.current;
        if (!img) return { x: ox, y: oy };
        const z = currentZoom ?? zoom;
        const baseScale = CANVAS_SIZE / Math.min(img.naturalWidth, img.naturalHeight);
        const scale = baseScale * z;
        const drawW = img.naturalWidth * scale;
        const drawH = img.naturalHeight * scale;
        const cx = CANVAS_SIZE / 2;
        const cy = CANVAS_SIZE / 2;
        const baseX = cx - drawW / 2;
        const baseY = cy - drawH / 2;
        // image must cover: drawX <= 0 and drawX+drawW >= CANVAS_SIZE
        const maxOx = -baseX;
        const minOx = CANVAS_SIZE - baseX - drawW;
        const maxOy = -baseY;
        const minOy = CANVAS_SIZE - baseY - drawH;
        return {
            x: Math.max(minOx, Math.min(maxOx, ox)),
            y: Math.max(minOy, Math.min(maxOy, oy)),
        };
    }, [zoom]);

    // Re-clamp when zoom changes so image stays covering the circle
    useEffect(() => {
        if (!imgLoaded) return;
        setOffset(prev => clampOffset(prev.x, prev.y, zoom));
    }, [zoom, imgLoaded, clampOffset]);

    // Draw on canvas whenever image/zoom/offset changes
    useEffect(() => {
        if (!imgLoaded) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const r = CANVAS_SIZE / 2;
        const img = imgRef.current;
        const baseScale = CANVAS_SIZE / Math.min(img.naturalWidth, img.naturalHeight);
        const scale = baseScale * zoom;
        const drawW = img.naturalWidth * scale;
        const drawH = img.naturalHeight * scale;
        const drawX = CANVAS_SIZE / 2 - drawW / 2 + offset.x;
        const drawY = CANVAS_SIZE / 2 - drawH / 2 + offset.y;

        ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

        // Draw image clipped to circle
        ctx.save();
        ctx.beginPath();
        ctx.arc(r, r, r, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
        ctx.restore();

        // Dark overlay outside circle (evenodd fill trick)
        ctx.save();
        ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
        ctx.beginPath();
        ctx.rect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        ctx.arc(r, r, r, 0, Math.PI * 2, true);
        ctx.fill("evenodd");
        ctx.restore();

        // Red border on circle
        ctx.save();
        ctx.strokeStyle = "#dc2626";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(r, r, r - 2, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
    }, [imgLoaded, zoom, offset]);

    // Attach non-passive wheel listener so we can preventDefault
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const handleWheel = (e) => {
            e.preventDefault();
            setZoom(z => Math.min(3, Math.max(1, z - e.deltaY * 0.003)));
        };
        canvas.addEventListener("wheel", handleWheel, { passive: false });
        return () => canvas.removeEventListener("wheel", handleWheel);
    }, []);

    // --- Mouse handlers ---
    const handleMouseDown = (e) => {
        e.preventDefault();
        setDragging(true);
        dragStartRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    };

    const handleMouseMove = useCallback((e) => {
        if (!dragStartRef.current) return;
        const rawX = e.clientX - dragStartRef.current.x;
        const rawY = e.clientY - dragStartRef.current.y;
        setOffset(clampOffset(rawX, rawY));
    }, [clampOffset]);

    const handleMouseUp = () => {
        setDragging(false);
        dragStartRef.current = null;
    };

    // --- Touch handlers ---
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setDragging(true);
        dragStartRef.current = { x: touch.clientX - offset.x, y: touch.clientY - offset.y };
    };

    const handleTouchMove = useCallback((e) => {
        if (!dragStartRef.current) return;
        const touch = e.touches[0];
        const rawX = touch.clientX - dragStartRef.current.x;
        const rawY = touch.clientY - dragStartRef.current.y;
        setOffset(clampOffset(rawX, rawY));
    }, [clampOffset]);

    const handleTouchEnd = () => {
        setDragging(false);
        dragStartRef.current = null;
    };

    // Render cropped output and call onApply
    const handleApply = () => {
        const img = imgRef.current;
        if (!img) return;
        setApplying(true);

        const outputCanvas = document.createElement("canvas");
        outputCanvas.width = OUTPUT_SIZE;
        outputCanvas.height = OUTPUT_SIZE;
        const ctx = outputCanvas.getContext("2d");

        const scaleFactor = OUTPUT_SIZE / CANVAS_SIZE;
        const baseScale = CANVAS_SIZE / Math.min(img.naturalWidth, img.naturalHeight);
        const scale = baseScale * zoom * scaleFactor;
        const drawW = img.naturalWidth * scale;
        const drawH = img.naturalHeight * scale;
        const cx = OUTPUT_SIZE / 2;
        const cy = OUTPUT_SIZE / 2;
        const drawX = cx - drawW / 2 + offset.x * scaleFactor;
        const drawY = cy - drawH / 2 + offset.y * scaleFactor;

        ctx.beginPath();
        ctx.arc(cx, cy, OUTPUT_SIZE / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, drawX, drawY, drawW, drawH);

        outputCanvas.toBlob(async (blob) => {
            if (!blob) { setApplying(false); return; }
            const croppedFile = new File([blob], "avatar.webp", { type: "image/webp" });
            await onApply(croppedFile);
            setApplying(false);
        }, "image/webp", 0.92);
    };

    return (
        <div className="avatar-editor-overlay" onClick={onCancel}>
            <div className="avatar-editor-modal" onClick={e => e.stopPropagation()}>
                <h3 className="avatar-editor-title">Adjust your photo</h3>
                <p className="avatar-editor-hint">Drag to reposition &nbsp;·&nbsp; Scroll or slide to zoom</p>

                <div className="avatar-editor-canvas-wrap">
                    {!imgLoaded && <div className="avatar-editor-loading" />}
                    <canvas
                        ref={canvasRef}
                        width={CANVAS_SIZE}
                        height={CANVAS_SIZE}
                        className={`avatar-editor-canvas${dragging ? " dragging" : ""}`}
                        style={{ display: imgLoaded ? "block" : "none" }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    />
                </div>

                <div className="avatar-editor-zoom">
                    <span className="avatar-editor-zoom-label avatar-editor-zoom-label--left">1×</span>
                    <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.01"
                        value={zoom}
                        onChange={e => setZoom(parseFloat(e.target.value))}
                        className="avatar-editor-slider"
                        aria-label="Zoom"
                    />
                    <span className="avatar-editor-zoom-label">3×</span>
                    <span className="avatar-editor-zoom-pct">{Math.round(zoom * 100)}%</span>
                </div>

                <div className="avatar-editor-actions">
                    <button className="avatar-editor-btn avatar-editor-btn--cancel" onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        className="avatar-editor-btn avatar-editor-btn--apply"
                        onClick={handleApply}
                        disabled={applying || !imgLoaded}
                    >
                        {applying ? "Saving…" : "Apply"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AvatarEditor;
